import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../config/api";
import { Button, Table, message, Modal, Spin, Form, Input, Card } from "antd";
import { useState, useEffect } from "react";
import EstimateFormModal from "./EstimateFormModal";
import IorCalculatorForm from "./TradeOS/TradeIorCalculatorForm";
// Import the new External Terms Popup
import ExternalIorTermsPopup from "./ExternalIorTermsPopup";
import { FaRegEye, FaDownload, FaLock, FaArrowLeft } from "react-icons/fa6";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { generateIOR } from "../shared/pdf/generateIOR";
import Logo from "../../assets/logo.png";
import { getSettings } from "../../lib/setting";
// Added import for fetching admins
import { getAllAdminUsers } from "../../lib/user";
import AskForEstimate from "../shared/askForQuestion/EstimateMessageComponent";

function ExternalLinkPage() {
  const { linkId, subpage } = useParams();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showTradeOS, setShowTradeOS] = useState(false);
  const [viewLoading, setViewLoading] = useState({});
  const [downloadLoading, setDownloadLoading] = useState({});
  // Added acceptLoading state
  const [acceptLoading, setAcceptLoading] = useState({});
  const [rejectLoading, setRejectLoading] = useState({});
  const [isPdfModalVisible, setIsPdfModalVisible] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [linkInfo, setLinkInfo] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [form] = Form.useForm();
  const [hasEstimatedInTradeOS, setHasEstimatedInTradeOS] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [currentEstimateForChat, setCurrentEstimateForChat] = useState(null);

  const ChatBubbleIcon = ({ className = "" }) => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M4 4H20C21.1 4 22 4.9 22 6V16C22 17.1 21.1 18 20 18H6L2 22V6C2 4.9 2.9 4 4 4Z"
        fill="currentColor"
      />
      <circle cx="8" cy="12" r="1.5" fill="white" />
      <circle cx="12" cy="12" r="1.5" fill="white" />
      <circle cx="16" cy="12" r="1.5" fill="white" />
    </svg>
  );

  // Sync showTradeOS state with subpage route param
  useEffect(() => {
    setShowTradeOS(subpage === "external_trade_os");
  }, [subpage]);

  // Fetch settings for TradeOS form
  const { data: settingsData, isLoading: settingsLoading } = useQuery(
    ["settings"],
    getSettings,
    {
      refetchOnWindowFocus: false,
    }
  );

  // Cache for estimate summaries
  const estimateSummaryCache = {};

  // Check if we have a stored token for this link
  useEffect(() => {
    const storedToken = localStorage.getItem(`external_token_${linkId}`);
    const storedExpiry = localStorage.getItem(
      `external_token_expiry_${linkId}`
    );

    if (storedToken && storedExpiry) {
      const expiryTime = parseInt(storedExpiry);
      if (Date.now() < expiryTime) {
        setAuthToken(storedToken);
        setIsAuthenticated(true);
        // Set up the API interceptor for this token
        setupApiInterceptor(storedToken);
      } else {
        // Token expired, remove from storage
        localStorage.removeItem(`external_token_${linkId}`);
        localStorage.removeItem(`external_token_expiry_${linkId}`);
      }
    }
  }, [linkId]);

  // Set up API interceptor for external link authentication
  const setupApiInterceptor = (token) => {
    // Add request interceptor to include token for external link requests
    const interceptor = api.interceptors.request.use(
      (config) => {
        // Only add external token for external link endpoints
        if (config.url && config.url.includes(`/external-links/${linkId}`)) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Store interceptor reference for cleanup
    window.externalLinkInterceptor = interceptor;
  };

  // Clean up interceptor on unmount
  useEffect(() => {
    return () => {
      if (window.externalLinkInterceptor) {
        api.interceptors.request.eject(window.externalLinkInterceptor);
        delete window.externalLinkInterceptor;
      }
    };
  }, []);

  // Fetch and validate the external link
  const {
    data: link,
    isLoading: linkLoading,
    error: linkError,
  } = useQuery(
    ["externalLink", linkId],
    async () => {
      const res = await api.get(`/external-links/${linkId}`);
      setLinkInfo(res.data);
      return res.data;
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  // Fetch estimates for the link
  const {
    data: estimates,
    isLoading: estimatesLoading,
    refetch: refetchEstimates,
  } = useQuery(
    ["estimates", linkId],
    async () => {
      const res = await api.get(`/external-links/${linkId}/estimates`);
      return res.data;
    },
    {
      enabled: isAuthenticated,
      retry: false,
      refetchInterval: 4000,
      refetchIntervalInBackground: true,
      staleTime: 5000,
    }
  );

  // Fetch link stats to get current estimate counts 
  const { data: linkStats, isLoading: statsLoading, refetch: refetchStats } = useQuery(
    ["linkStats", linkId],
    async () => {
      const res = await api.get(`/external-links/${linkId}/stats`);
      return res.data;
    },
    {
      enabled: isAuthenticated,
      retry: false,
      onSuccess: (data) => {
        // Update linkInfo with current stats from server
        if (data.linkInfo) {
          setLinkInfo((prev) => ({
            ...prev,
            ...data.linkInfo,
            // Ensure we preserve any existing properties from the initial link fetch
            linkName: data.linkInfo.linkName || prev?.linkName,
            maxEstimates: data.linkInfo.maxEstimates,
            estimatesCreated: data.linkInfo.estimatesCreated,
          }));
        }
      },
    }
  );

  // **FIX: Refetch estimates and stats when returning to main table view**
  useEffect(() => {
    if (!showTradeOS && isAuthenticated) {
      // Refetch both estimates and stats when coming back to main view
      refetchEstimates();
      refetchStats();
    }
  }, [showTradeOS, isAuthenticated, refetchEstimates, refetchStats]);

  // Handle password authentication
  const handleAuthentication = async (values) => {
    setAuthLoading(true);
    try {
      const response = await api.post(
        `/external-links/${linkId}/authenticate`,
        {
          password: values.password,
        }
      );

      if (response.data.success) {
        const { token, linkName, maxEstimates, estimatesCreated } =
          response.data;

        // Store token and expiry (24 hours)
        const expiryTime = Date.now() + 24 * 60 * 60 * 1000;
        localStorage.setItem(`external_token_${linkId}`, token);
        localStorage.setItem(
          `external_token_expiry_${linkId}`,
          expiryTime.toString()
        );

        setAuthToken(token);
        setIsAuthenticated(true);
        setLinkInfo((prev) => ({
          ...prev,
          linkName,
          maxEstimates,
          estimatesCreated,
        }));

        // Set up the API interceptor
        setupApiInterceptor(token);

        message.success("Authentication successful!");
        form.resetFields();
      }
    } catch (error) {
      console.error("Authentication error:", error);
      const errorMessage =
        error.response?.data?.error || "Authentication failed";
      message.error(errorMessage);
    } finally {
      setAuthLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem(`external_token_${linkId}`);
    localStorage.removeItem(`external_token_expiry_${linkId}`);
    setAuthToken(null);
    setIsAuthenticated(false);
    setLinkInfo((prev) => (prev ? { ...prev, requiresAuth: true } : null));

    // Clean up interceptor
    if (window.externalLinkInterceptor) {
      api.interceptors.request.eject(window.externalLinkInterceptor);
      delete window.externalLinkInterceptor;
    }

    message.info("Logged out successfully");
  };

  const fetchEstimateSummary = async (estimateId) => {
    if (estimateSummaryCache[estimateId]) {
      return estimateSummaryCache[estimateId];
    }

    try {
      const response = await api.get(
        `/external-links/${linkId}/estimates/${estimateId}/summary`
      );

      // Always cache even if partial
      estimateSummaryCache[estimateId] = response.data.estimateSummary || {};
      return response.data.estimateSummary || {};
    } catch (error) {
      console.error("Summary API failed:", error.response?.data || error);
      message.error("Could not load estimate details for PDF");
      return {};
    }
  };

  // Handle successful estimate submission
  const handleEstimateSubmission = () => {
    message.success("Estimate created successfully");
    refetchEstimates();
    refetchStats();
    setIsModalOpen(false);
    if (linkInfo) {
      setLinkInfo((prev) => ({
        ...prev,
        estimatesCreated: (prev.estimatesCreated || 0) + 1,
      }));
    }
  };

  // Handle TradeOS submission
  const handleTradeOSSubmission = () => {
    message.success("TradeOS estimate created successfully");
    refetchEstimates();
    refetchStats();
    if (linkInfo) {
      setLinkInfo((prev) => ({
        ...prev,
        estimatesCreated: (prev.estimatesCreated || 0) + 1,
      }));
    }
    navigate(`/guest-estimate/${linkId}`);
  };

  // Handle View PDF
  const handleViewPdf = async (estimate) => {
    setViewLoading((prev) => ({ ...prev, [estimate._id]: true }));
    try {
      const estimateSummary = await fetchEstimateSummary(estimate._id);
      if (!estimateSummary?.services) {
        throw new Error("Failed to fetch estimate summary");
      }

      // FILTER LOGIC FOR 0 VALUES
      const filteredServices = estimateSummary.services.filter((service) => {
        const amount = service.total ?? service.value ?? service.exclVAT ?? 0;
        const num = parseFloat(amount);
        return !isNaN(num) && num > 0;
      });

      const modifiedDhlEstimation = estimate.dhlEstimation
        ? {
          ...estimate.dhlEstimation,
          feeTotals: (estimateSummary.dhlTaxes || []).map((tax) => ({
            name: tax.name,
            currency: estimateSummary.currency || "USD",
            value: tax.value,
          })),
        }
        : null;

      const pdfBlob = await generateIOR(
        estimate,
        { ...estimateSummary, services: filteredServices },
        modifiedDhlEstimation
      );
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setCurrentPdfUrl(pdfUrl);
      setIsPdfModalVisible(true);
    } catch (error) {
      console.error("Error generating PDF:", error);
      message.error(`Error generating PDF: ${error.message}`);
    } finally {
      setViewLoading((prev) => ({ ...prev, [estimate._id]: false }));
    }
  };

  // Handle Download PDF
  const handleDownloadPdf = async (estimate) => {
    setDownloadLoading((prev) => ({ ...prev, [estimate._id]: true }));
    try {
      const estimateSummary = await fetchEstimateSummary(estimate._id);
      if (!estimateSummary?.services) {
        throw new Error("Failed to fetch estimate summary");
      }

      // FILTER LOGIC FOR 0 VALUES
      const filteredServices = estimateSummary.services.filter((service) => {
        const amount = service.total ?? service.value ?? service.exclVAT ?? 0;
        const num = parseFloat(amount);
        return !isNaN(num) && num > 0;
      });

      const modifiedDhlEstimation = estimate.dhlEstimation
        ? {
          ...estimate.dhlEstimation,
          feeTotals: estimateSummary.dhlTaxes.map((tax) => ({
            name: tax.name,
            currency: estimateSummary.currency,
            value: tax.value,
          })),
        }
        : null;

      const pdfBlob = await generateIOR(
        estimate,
        { ...estimateSummary, services: filteredServices },
        modifiedDhlEstimation
      );
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `estimate_${estimate.estimateId || estimate._id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(pdfUrl);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      message.error(`Error downloading PDF: ${error.message}`);
    } finally {
      setDownloadLoading((prev) => ({ ...prev, [estimate._id]: false }));
    }
  };

  // Handle Accept Estimate
  const handleAcceptEstimate = async (estimate) => {
    setAcceptLoading((prev) => ({ ...prev, [estimate._id]: true }));
    try {
      // 1. Fetch all admin-role users
      const admins = await getAllAdminUsers();
      const adminEmails = admins
        .map((admin) => admin.email)
        .filter((email) => email);

      let emailSent = false;
      if (adminEmails.length) {
        const emailResponse = await api.post('/mail/send-external-accept-notification', {
          estimateData: estimate,
          adminEmails: adminEmails,
          linkName: linkInfo?.linkName || "External Link"
        });
        emailSent = Boolean(emailResponse?.data?.success);
      }

      // 2. Send system notification even if email recipients are unavailable
      await api.post(`/external-links/${linkId}/estimates/${estimate._id}/accept-notify`);

      if (emailSent) {
        message.success("Acceptance notification sent to Admins.");
      } else if (!adminEmails.length) {
        message.warning("Acceptance saved and admin notification created, but no admin email recipients were found.");
      } else {
        message.warning("Email notification might not have been sent, but admin system notification was triggered.");
      }

      // Refetch to show updated status if applicable
      refetchEstimates();

    } catch (error) {
      console.error("Error accepting estimate:", error);
      message.error("Failed to process acceptance: " + (error.response?.data?.message || error.message));
    } finally {
      setAcceptLoading((prev) => ({ ...prev, [estimate._id]: false }));
    }
  };

  const handleRejectEstimate = async (estimate) => {
    setRejectLoading((prev) => ({ ...prev, [estimate._id]: true }));
    try {
      await api.post(`/external-links/${linkId}/estimates/${estimate._id}/reject-notify`);
      message.success("Rejection notification sent to Admins.");
      refetchEstimates();
    } catch (error) {
      console.error("Error rejecting estimate:", error);
      message.error("Failed to process rejection: " + (error.response?.data?.message || error.message));
    } finally {
      setRejectLoading((prev) => ({ ...prev, [estimate._id]: false }));
    }
  };

  const handleChatClick = (estimate) => {
    setCurrentEstimateForChat(estimate);
    setShowCommentModal(true);
  };

  const getUnreadChatCount = (estimate) => {
    if (!estimate || (showCommentModal && currentEstimateForChat?._id === estimate._id)) {
      return 0;
    }

    if (!Array.isArray(estimate.comments)) {
      return 0;
    }

    return estimate.comments.filter(
      (comment) => comment?.user === "admin" && comment?.status !== "read"
    ).length;
  };

  // Check if user can create more estimates
  const canCreateEstimate = () => {
    if (!linkInfo) return false;
    if (linkInfo.maxEstimates === "unlimited") return true;
    return (linkInfo.estimatesCreated || 0) < parseInt(linkInfo.maxEstimates);
  };

  // Calculate remaining estimates for display
  const getRemainingEstimates = () => {
    if (!linkInfo) return 0;
    if (linkInfo.maxEstimates === "unlimited") return "unlimited";
    return parseInt(linkInfo.maxEstimates) - (linkInfo.estimatesCreated || 0);
  };

  if (linkLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (linkError || !link?.success) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Card className="max-w-md w-full">
          <div className="text-center">
            <FaLock className="text-6xl text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">
              Invalid Link
            </h2>
            <p className="text-gray-600">
              This estimation link is invalid, expired, or has been disabled.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // Show authentication form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <Card className="max-w-md w-full">
          <div className="text-center mb-6">
            <div className="flex justify-start mb-6">
              <img className="w-32" src={Logo} alt="logo" />
            </div>
            <FaLock className="text-4xl text-blue-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">
              {linkInfo?.linkName || "Secure Estimation Link"}
            </h2>
            <p className="text-gray-600">
              Please enter the password to access this estimation form.
            </p>
          </div>

          <Form form={form} onFinish={handleAuthentication} layout="vertical">
            <Form.Item
              name="password"
              label="Access Password"
              rules={[{ required: true, message: "Please enter the password" }]}
            >
              <Input.Password placeholder="Enter password" size="large" />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={authLoading}
                size="large"
                className="w-full bg-blue-600"
              >
                Access Estimation Form
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    );
  }

  const columns = [
    {
      title: "Estimate ID",
      dataIndex: "estimateId",
      key: "estimateId",
      align: "center",
    },
    {
      title: "User Name",
      dataIndex: "userName",
      key: "userName",
      align: "center",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      align: "center",
    },
    {
      title: "Creation Date",
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      render: (text) => new Date(text).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (text, record) => (
        <div className="flex justify-center gap-3">
          {(() => {
            const responseStatus = record.customerResponseStatus || (record.estimationAccept ? "accepted" : "pending");
            const hasResponded = responseStatus === "accepted" || responseStatus === "rejected";

            return (
              <>
                <button
                  onClick={() => handleAcceptEstimate(record)}
                  className="py-1 px-3 rounded-md bg-[#7CE13E] text-white hover:bg-green-600 transition-colors disabled:cursor-not-allowed"
                  title="Accept Estimate & Notify Admins"
                  disabled={acceptLoading[record._id] || hasResponded}
                >
                  {acceptLoading[record._id] ? (
                    <Spin size="small" />
                  ) : (
                    <FaCheckCircle className="text-2xl" />
                  )}
                </button>
                <button
                  onClick={() => handleRejectEstimate(record)}
                  className="py-1 px-3 rounded-md bg-[#EF4444] text-white hover:bg-red-600 transition-colors disabled:cursor-not-allowed"
                  title="Reject Estimate & Notify Admins"
                  disabled={rejectLoading[record._id] || hasResponded}
                >
                  {rejectLoading[record._id] ? (
                    <Spin size="small" />
                  ) : (
                    <FaTimesCircle className="text-2xl" />
                  )}
                </button>
                <div className="relative">
                  <button
                    onClick={() => handleChatClick(record)}
                    className="py-1 px-3 rounded-md bg-[#A9A9A9] text-white hover:bg-gray-600 transition-colors"
                    title="Chat"
                  >
                    <ChatBubbleIcon className="h-6 w-6" />
                  </button>
                  {getUnreadChatCount(record) > 0 && (
                    <div className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 rounded-full bg-green-500 text-white text-[11px] font-semibold flex items-center justify-center">
                      {getUnreadChatCount(record)}
                    </div>
                  )}
                </div>
              </>
            );
          })()}

          {/* View Estimate */}
          <button
            onClick={() => handleViewPdf(record)}
            className="py-1 px-3 rounded-md bg-[#A9A9A9] text-white hover:bg-gray-600 transition-colors"
            disabled={viewLoading[record._id]}
            title="View PDF"
          >
            {viewLoading[record._id] ? (
              <Spin size="small" />
            ) : (
              <FaRegEye className="text-2xl" />
            )}
          </button>
          {/* Download Estimate */}
          <button
            onClick={() => handleDownloadPdf(record)}
            className="py-1 px-3 rounded-md bg-[#3861DC] text-white hover:bg-blue-700 transition-colors"
            disabled={downloadLoading[record._id]}
            title="Download PDF"
          >
            {downloadLoading[record._id] ? (
              <Spin size="small" />
            ) : (
              <FaDownload className="text-2xl" />
            )}
          </button>
        </div>
      ),
    },
  ];

  // If TradeOS is active, render TradeOS view
  if (showTradeOS) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        {/* Add Terms Popup here for TradeOS view */}
        <ExternalIorTermsPopup linkId={linkId} onLogout={handleLogout} />

        <div className="flex justify-start p-4 pt-2">
          <img className="w-32" src={Logo} alt="logo" />
        </div>

        <div className="flex-1 p-4 overflow-auto">
          {!hasEstimatedInTradeOS && (
            <div className="mb-6 flex justify-start pl-12">
              <Button
                onClick={() => navigate(`/guest-estimate/${linkId}`)}
                className="btnBg text-white flex items-center gap-2"
                icon={<FaArrowLeft />}
              >
                Back
              </Button>
            </div>
          )}

          <div className="min-h-full">
            <IorCalculatorForm
              settingsData={settingsData}
              settingsLoading={settingsLoading}
              isGuest={true}
              linkId={linkId}
              onSubmit={handleTradeOSSubmission}
              onEstimationStateChange={setHasEstimatedInTradeOS}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      {/* Add Terms Popup here for Main Dashboard View */}
      <ExternalIorTermsPopup linkId={linkId} onLogout={handleLogout} />

      <div className="flex justify-start mb-6">
        <img className="w-32" src={Logo} alt="logo" />
      </div>

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              {linkInfo?.linkName || "External Estimates"}
            </h1>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              {statsLoading ? (
                <Spin size="small" />
              ) : (
                <>
                  <span>
                    Estimates: {linkInfo?.estimatesCreated || 0}
                    {linkInfo?.maxEstimates !== "unlimited" && (
                      <span> / {linkInfo?.maxEstimates}</span>
                    )}
                  </span>
                  {linkInfo?.maxEstimates !== "unlimited" && (
                    <span>Remaining: {getRemainingEstimates()}</span>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              className="btnBg text-white"
              onClick={() =>
                navigate(`/guest-estimate/${linkId}/external_trade_os`)
              }
              disabled={!canCreateEstimate() || statsLoading || settingsLoading}
              title={
                !canCreateEstimate()
                  ? "Maximum estimates reached"
                  : "Open TradeOS"
              }
            >
              TradeOS
            </Button>
            <Button
              onClick={handleLogout}
              className="text-gray-600 border-gray-300"
            >
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Estimates Table */}
      <div className="bg-white rounded-lg shadow-sm">
        <Table
          columns={columns}
          dataSource={estimates}
          loading={estimatesLoading}
          rowKey="_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} estimates`,
          }}
        />
      </div>

      {/* Estimate Form Modal */}
      {isModalOpen && (
        <EstimateFormModal
          isVisible={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleEstimateSubmission}
          isGuest={true}
          linkId={linkId}
          isModal={true}
        />
      )}

      <Modal
        title={
          <div className="flex flex-col">
            <span>Chat</span>
            <span className="text-xs opacity-60">
              Proposal ID: {currentEstimateForChat?.estimateId ?? ""}
            </span>
          </div>
        }
        open={showCommentModal}
        onCancel={() => {
          setShowCommentModal(false);
          setCurrentEstimateForChat(null);
          setTimeout(() => {
            refetchEstimates();
          }, 250);
        }}
        footer={null}
        width={900}
        destroyOnClose
        styles={{ body: { padding: 0 } }}
      >
        {showCommentModal && currentEstimateForChat && (
          <AskForEstimate
            id={currentEstimateForChat._id}
            estimateId={currentEstimateForChat.estimateId}
            proposalId={currentEstimateForChat.estimateId}
            guestUserName={currentEstimateForChat.userName}
            guestUserEmail={currentEstimateForChat.email}
            presenceUserId={currentEstimateForChat.email || `guest-estimate-${currentEstimateForChat.estimateId}`}
            notificationLink={`/newRequest?tab=external-estimates&estimateId=${currentEstimateForChat.estimateId}&openChat=true`}
            notificationType="est"
            containerClassName="h-[56vh]"
            fetchCommentsUrl={`/external-links/${linkId}/estimates/${currentEstimateForChat._id}/comments`}
            addCommentUrl={`/external-links/${linkId}/estimates/${currentEstimateForChat._id}/comments`}
            syncCommentsUrl={`/external-links/${linkId}/estimates/${currentEstimateForChat._id}/comments/sync`}
            attachmentUploadUrl={`/external-links/${linkId}/estimates/${currentEstimateForChat._id}/comments/upload`}
            disableFrontendNotifications={true}
            showPresenceStatus={false}
            isGuestSender={true}
          />
        )}
      </Modal>

      {/* PDF Preview Modal */}
      {isPdfModalVisible && (
        <Modal
          title="Estimate Preview"
          visible={isPdfModalVisible}
          onCancel={() => {
            setIsPdfModalVisible(false);
            if (currentPdfUrl) {
              URL.revokeObjectURL(currentPdfUrl);
              setCurrentPdfUrl(null);
            }
          }}
          footer={null}
          width="80%"
          style={{ top: 20 }}
        >
          <iframe
            src={`${currentPdfUrl}#toolbar=0`}
            title="Estimate PDF"
            style={{ width: "100%", height: "70vh", border: 0 }}
          />
        </Modal>
      )}
    </div>
  );
}

export default ExternalLinkPage;
